import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { LeaderboardTable } from "./LeaderboardTable";
import { ProfileNameModal } from "./ProfileNameModal";
import {
  LEADERBOARD_LIMIT,
  LEADERBOARD_METRIC_OPTIONS,
  LEADERBOARD_PERIOD_OPTIONS,
  getLeaderboardMetricLabel,
  type LeaderboardMetric,
  type LeaderboardPeriod
} from "../game/leaderboard";
import { fetchGlobalLeaderboard, type LeaderboardEntry, type LeaderboardSnapshot } from "../lib/leaderboard";
import {
  buildLeaderboardRows,
  fillLeaderboardRows,
  getLeaderboardErrorMessage
} from "./leaderboardTableModel";

type LeaderboardScreenProps = {
  activeMetric: LeaderboardMetric;
  activePeriod: LeaderboardPeriod;
  currentUserId: string | null;
  currentProfileName: string;
  highlightEntries: Partial<Record<LeaderboardMetric, LeaderboardEntry>>;
  refreshKey: number;
  onMetricChange: (metric: LeaderboardMetric) => void;
  onPeriodChange: (period: LeaderboardPeriod) => void;
  onSaveProfileName: (profileName: string) => void;
  onBack: () => void;
};

export function LeaderboardScreen({
  activeMetric,
  activePeriod,
  currentUserId,
  currentProfileName,
  highlightEntries,
  refreshKey,
  onMetricChange,
  onPeriodChange,
  onSaveProfileName,
  onBack
}: LeaderboardScreenProps) {
  const [snapshot, setSnapshot] = useState<LeaderboardSnapshot>({
    entries: [],
    highlightedEntry: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [isProfileNameModalOpen, setIsProfileNameModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setErrorMessage(null);

    void (async () => {
      try {
        const nextSnapshot = await fetchGlobalLeaderboard(
          activeMetric,
          activePeriod,
          highlightEntries[activeMetric]?.id ?? null
        );

        if (cancelled) {
          return;
        }

        setSnapshot(nextSnapshot);
      } catch (error) {
        if (cancelled) {
          return;
        }

        setSnapshot({
          entries: [],
          highlightedEntry: null
        });
        setErrorMessage(getLeaderboardErrorMessage(error, "Could not load leaderboard."));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeMetric, activePeriod, highlightEntries, refreshKey, retryNonce]);

  const rows = useMemo(
    () =>
      fillLeaderboardRows(
        buildLeaderboardRows({
          entries: snapshot.entries,
          highlightedEntry: snapshot.highlightedEntry,
          currentUserId,
          currentProfileName,
          limit: LEADERBOARD_LIMIT
        }),
        LEADERBOARD_LIMIT
      ),
    [currentProfileName, currentUserId, snapshot.entries, snapshot.highlightedEntry]
  );
  const metricLabel = getLeaderboardMetricLabel(activeMetric);
  const transitionKey = `${activeMetric}-${activePeriod}`;

  return (
    <motion.section
      className="menu-screen leaderboard-screen"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -18 }}
      transition={{ duration: 0.24, ease: "easeOut" }}
    >
      <div className="leaderboard-card leaderboard-cabinet panel">
        <div className="leaderboard-header">
          <h2 className="leaderboard-title">LEADERBOARD</h2>
        </div>

        <div className="leaderboard-controls">
          <div className="leaderboard-toggle-group" role="tablist" aria-label="Leaderboard metric">
            {LEADERBOARD_METRIC_OPTIONS.map((option) => {
              const active = activeMetric === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`leaderboard-toggle${active ? " leaderboard-toggle-active" : ""}`}
                  onClick={() => onMetricChange(option.value)}
                >
                  {active ? (
                    <motion.span
                      layoutId="leaderboard-metric-pill"
                      className="leaderboard-toggle-pill"
                    />
                  ) : null}
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>

          <div
            className="leaderboard-toggle-group leaderboard-toggle-group-period"
            role="tablist"
            aria-label="Leaderboard period"
          >
            {LEADERBOARD_PERIOD_OPTIONS.map((option) => {
              const active = activePeriod === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`leaderboard-toggle${active ? " leaderboard-toggle-active" : ""}`}
                  onClick={() => onPeriodChange(option.value)}
                >
                  {active ? (
                    <motion.span
                      layoutId="leaderboard-period-pill"
                      className="leaderboard-toggle-pill"
                    />
                  ) : null}
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="leaderboard-table-shell">
          <LeaderboardTable
            metricLabel={metricLabel}
            rows={rows}
            isLoading={isLoading}
            errorMessage={errorMessage}
            transitionKey={transitionKey}
            onRetry={() => setRetryNonce((currentValue) => currentValue + 1)}
            onRequestChangeName={() => setIsProfileNameModalOpen(true)}
          />
        </div>
      </div>

      <div className="menu-actions leaderboard-actions">
        <button type="button" className="menu-button menu-button-secondary" onClick={onBack}>
          BACK
        </button>
      </div>

      <ProfileNameModal
        open={isProfileNameModalOpen}
        initialValue={currentProfileName}
        onClose={() => setIsProfileNameModalOpen(false)}
        onSave={onSaveProfileName}
      />
    </motion.section>
  );
}
